import { Fragment, useCallback, useRef } from 'react';
import { useFormikContext } from 'formik';
import { deletePanel, type PanelConfigPatch, updatePanelConfig, updatePanelSize } from '../common/panel-grid';
import { type PageCmsPanels, type PanelSize } from '../common/panel-types';
import { PanelGrid } from '../CustomPage.styled';
import { usePanelDragMonitor } from '../hooks/usePanelDragMonitor';
import { PanelGridRow } from './PanelGridRow';
import { PanelGridRowDropZone } from './PanelGridRowDropZone';

interface PanelGridFormValues {
  panels: PageCmsPanels;
}

interface PanelGridLayoutProps {
  readonly showPanelErrors: boolean;
}

export const PanelGridLayout = ({ showPanelErrors }: PanelGridLayoutProps) => {
  const { values, setFieldValue } = useFormikContext<PanelGridFormValues>();
  const { panels } = values;

  // The callbacks read the grid through a ref so they stay referentially stable: editing one panel
  // then re-renders only its own memoized row.
  const panelsRef = useRef(panels);
  panelsRef.current = panels;

  const handleSizeChange = useCallback(
    (rowIndex: number, panelId: string, size: PanelSize) =>
      setFieldValue('panels', updatePanelSize(panelsRef.current, rowIndex, panelId, size)),
    [setFieldValue]
  );

  const handleDeletePanel = useCallback(
    (rowIndex: number, panelId: string) => setFieldValue('panels', deletePanel(panelsRef.current, rowIndex, panelId)),
    [setFieldValue]
  );

  const handleConfigChange = useCallback(
    (rowIndex: number, panelId: string, patch: PanelConfigPatch) =>
      setFieldValue('panels', updatePanelConfig(panelsRef.current, rowIndex, panelId, patch)),
    [setFieldValue]
  );

  const handleDrop = useCallback((next: PageCmsPanels) => setFieldValue('panels', next), [setFieldValue]);
  const { isDragActive, dropPreview } = usePanelDragMonitor(panels, handleDrop);

  return (
    <PanelGrid data-testid="panel-grid-layout">
      <PanelGridRowDropZone rowIndex={0} isDragActive={isDragActive} />
      {panels.map((row, rowIndex) => (
        // Rows have no id of their own; keying by panel ids remounts a row exactly when its membership changes.
        <Fragment key={row.map((panel) => panel.id).join(':')}>
          <PanelGridRow
            row={row}
            rowIndex={rowIndex}
            onSizeChange={handleSizeChange}
            onDeletePanel={handleDeletePanel}
            onConfigChange={handleConfigChange}
            showPanelErrors={showPanelErrors}
            dropPreview={dropPreview?.rowIndex === rowIndex ? dropPreview : null}
          />
          <PanelGridRowDropZone rowIndex={rowIndex + 1} isDragActive={isDragActive} />
        </Fragment>
      ))}
    </PanelGrid>
  );
};
